/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { Checkbox } from '../../ts-components/Checkbox'
import { SwitchInput } from '../../ts-components/SwitchInput'
import { FormAction } from '../reducer/useFeedPostReducer'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import {
  UNJOINED_SWITCH_DESCRIPTION,
  UNJOINED_SWITCH_TITLE,
  unjoinedSwitchSubtitle,
} from '../utils/audienceReach'

interface CommunicationChannelProps {
  postToFeed: boolean
  sendViaEmail: boolean
  sendViaPush: boolean
  /** Whether the post will also email roster people who hold no account (SN-7901). */
  includeUnjoined: boolean
  /**
   * How many more people the switch reaches, from the audience payload. Null before the
   * audience has been calculated.
   */
  unjoinedAvailable: number | null
  dispatch: React.Dispatch<FormAction>
  fieldErrors: string[]
  customError: any
  name: string
}

/**
 * CommunicationChannel component - handles post distribution method toggles
 *
 * This component manages the switches for different communication channels:
 * - Post to Feed: Whether to publish on the social feed
 * - Send via Email: Whether to notify audience by email
 * - Send via Push Notification: Whether to send push notifications
 *
 * The component only displays when the selected entity is an organization
 * and the user has appropriate permissions.
 *
 * @param props - Component props for form state and dispatch function
 * @returns JSX element representing the communication channel toggles
 */
export const CommunicationChannel: React.FC<CommunicationChannelProps> = ({
  postToFeed,
  sendViaEmail,
  sendViaPush,
  includeUnjoined,
  unjoinedAvailable,
  dispatch,
  name,
  fieldErrors,
  customError,
}) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-self: stretch;
        width: 100%;
      `}
    >
      <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
        Communication Channels *
      </Text>

      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        `}
      >
        <Checkbox
          label='Social Feed'
          hint='Share this update on the main social feed for everyone to see.'
          name='postToFeed'
          value={postToFeed}
          onChange={(e) =>
            dispatch({
              type: 'SET_FIELD',
              field: 'postToFeed',
              payload: e.target.checked,
            })
          }
        />

        <Checkbox
          label='Email'
          hint='Notify the selected audience by email.'
          name='sendViaEmail'
          value={sendViaEmail}
          onChange={(e) =>
            dispatch({
              type: 'SET_FIELD',
              field: 'sendViaEmail',
              payload: e.target.checked,
            })
          }
        />

        <Checkbox
          label='Push Notification'
          hint="Send a push notification to the selected audience's mobile device."
          name='sendViaPush'
          value={sendViaPush}
          onChange={(e) =>
            dispatch({
              type: 'SET_FIELD',
              field: 'sendViaPush',
              payload: e.target.checked,
            })
          }
        />
      </div>

      {/*
        A contextual sub-control of the Email checkbox, so it appears with it. The
        SUB-LABEL is what greys at zero rather than disappearing — a switch whose
        explanation vanishes reads as a bug.
      */}
      {sendViaEmail && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            width: 100%;
            padding: 12px;
            border-radius: 6px;
            background: ${colors.SECONDARY_200};
          `}
        >
          <SwitchInput
            title={UNJOINED_SWITCH_TITLE}
            subtitle={
              <>
                {UNJOINED_SWITCH_DESCRIPTION}
                <br />
                {unjoinedAvailable === null
                  ? 'Calculate the audience to see how many this adds.'
                  : unjoinedSwitchSubtitle(unjoinedAvailable)}
              </>
            }
            // NEUTRAL_100 was too dim to read comfortably; NEUTRAL_40 keeps it secondary
            // to the label without making anyone squint.
            subtitleColor={colors.NEUTRAL_40}
            value={includeUnjoined}
            onChange={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'includeUnjoined',
                payload: value,
              })
            }
          />
        </div>
      )}

      <ErrorLabel
        fieldErrors={fieldErrors}
        customError={customError}
        name={name}
      />
    </div>
  )
}
